import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Settings, Flame, Target, Trophy, Clock, 
  RefreshCw, Pause, Play, X, User, BarChart3
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import Flashcard from '../components/Flashcard';
import CircleOfFifths from '../components/CircleOfFifths';
import WelcomeBanner from '../components/WelcomeBanner';
import StatsCard from '../components/StatsCard';
import KeySelector from '../components/KeySelector';
import ReferenceChart from '../components/ReferenceChart';
import { generateQuestion } from '../lib/nashville_theory';
import useStore from '../store/useStore';
import useAuthStore from '../store/useAuthStore';
import { cn } from '../lib/utils';

const Practice = () => {
  const navigate = useNavigate();
  const { token, refreshUser } = useAuthStore();
  const { 
    selectedKeys, 
    practiceMode, 
    setPracticeMode,
    sessionStats, 
    resetSession, 
    recordAnswer,
    timerEnabled,
    setTimerEnabled,
    timerDuration,
    setTimerDuration
  } = useStore();

  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [showAnswer, setShowAnswer] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const questionDetailsRef = useRef([]);
  const sessionStartRef = useRef(Date.now());
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const saveSession = useCallback(async () => {
    if (sessionStats.questionsAnswered < 1 || !token) return;
    try {
      await fetch(`${BACKEND_URL}/api/progress/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          mode: practiceMode,
          keys_practiced: selectedKeys,
          questions_answered: sessionStats.questionsAnswered,
          correct: sessionStats.correct,
          incorrect: sessionStats.incorrect,
          best_streak: sessionStats.bestStreak,
          duration_seconds: Math.round((Date.now() - sessionStartRef.current) / 1000),
          question_details: questionDetailsRef.current
        })
      });
    } catch (e) {
      console.error('Failed to save session:', e);
    }
  }, [sessionStats, practiceMode, selectedKeys, token, BACKEND_URL]);

  useEffect(() => {
    refreshUser();
  }, []);

  // Generate initial question
  useEffect(() => {
    if (selectedKeys.length > 0 && !currentQuestion) {
      resetSession();
      generateNewQuestion();
    }
  }, [selectedKeys]);

  const generateNewQuestion = useCallback(() => {
    if (selectedKeys.length === 0) return;
    const question = generateQuestion(selectedKeys, practiceMode);
    setCurrentQuestion(question);
    setShowAnswer(false);
  }, [selectedKeys, practiceMode]);

  const handleAnswer = (isCorrect) => {
    recordAnswer(isCorrect);
    if (currentQuestion) {
      questionDetailsRef.current.push({
        key: currentQuestion.key,
        number: currentQuestion.number,
        mode: currentQuestion.type,
        correct: isCorrect
      });
    }
  };

  const handleNext = () => {
    generateNewQuestion();
  };

  const handleModeChange = (mode) => {
    // Save current session before switching modes
    if (practiceMode !== 'circle_of_fifths' && sessionStats.questionsAnswered > 0) {
      saveSession();
    }
    setPracticeMode(mode);
    if (mode !== 'circle_of_fifths') {
      questionDetailsRef.current = [];
      sessionStartRef.current = Date.now();
      setTimeout(() => {
        const question = generateQuestion(selectedKeys, mode);
        setCurrentQuestion(question);
        setShowAnswer(false);
      }, 0);
    }
  };

  const accuracy = sessionStats.questionsAnswered > 0 
    ? Math.round((sessionStats.correct / sessionStats.questionsAnswered) * 100)
    : 0;

  if (selectedKeys.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" data-testid="no-keys-message">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">No keys selected</h2>
          <p className="text-muted-foreground mb-6">
            Please select at least one key to practice.
          </p>
          <Button onClick={() => setSettingsOpen(true)} data-testid="select-keys-btn">
            Select Keys
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen noise-bg" data-testid="practice-page">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={async () => {
                  await saveSession();
                  navigate('/');
                }}
                className="btn-press"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-xl font-bold hidden md:block">Practice</h1>
            </div>

            {/* Mode Tabs */}
            <Tabs value={practiceMode} onValueChange={handleModeChange}>
              <TabsList className="bg-secondary" data-testid="mode-tabs">
                <TabsTrigger 
                  value="number_to_chord" 
                  className="data-[state=active]:bg-accent data-[state=active]:text-white"
                  data-testid="mode-number-to-chord"
                >
                  # → Chord
                </TabsTrigger>
                <TabsTrigger 
                  value="chord_to_number"
                  className="data-[state=active]:bg-accent data-[state=active]:text-white"
                  data-testid="mode-chord-to-number"
                >
                  Chord → #
                </TabsTrigger>
                <TabsTrigger 
                  value="circle_of_fifths"
                  className="data-[state=active]:bg-accent data-[state=active]:text-white"
                  data-testid="mode-circle-of-fifths"
                >
                  Circle of 5ths
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="flex items-center gap-2">
              <ReferenceChart />
              <Button 
                variant="outline" 
                size="icon" 
                className="btn-press" 
                onClick={() => navigate('/progress')}
                data-testid="progress-btn"
              >
                <BarChart3 className="w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                className="btn-press" 
                onClick={() => navigate('/account')}
                data-testid="account-btn"
              >
                <User className="w-5 h-5" />
              </Button>
              <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="icon" className="btn-press" data-testid="settings-btn">
                    <Settings className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent data-testid="settings-panel">
                  <SheetHeader>
                    <SheetTitle>Practice Settings</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-8">
                    {/* Timer Settings */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">Timed Mode</h4>
                          <p className="text-sm text-muted-foreground">
                            {timerDuration}s per question
                          </p>
                        </div>
                        <Switch 
                          checked={timerEnabled} 
                          onCheckedChange={setTimerEnabled}
                          data-testid="timer-switch"
                        />
                      </div>
                      {timerEnabled && (
                        <div className="flex gap-2">
                          {[5, 10, 15, 20].map(duration => (
                            <Button
                              key={duration}
                              variant={timerDuration === duration ? "default" : "outline"}
                              size="sm"
                              onClick={() => setTimerDuration(duration)}
                              className="btn-press"
                              data-testid={`timer-${duration}s`}
                            >
                              {duration}s
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Key Selection */}
                    <div>
                      <h4 className="font-medium mb-4">Practice Keys</h4>
                      <KeySelector />
                    </div>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 md:px-6 py-8 md:py-12">
        <WelcomeBanner />
        {practiceMode === 'circle_of_fifths' ? (
          <div className="max-w-lg mx-auto">
            <CircleOfFifths />
          </div>
        ) : (
        <>
        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 md:mb-12">
          <StatsCard 
            label="Correct" 
            value={sessionStats.correct}
            icon={Target}
          />
          <StatsCard 
            label="Streak" 
            value={sessionStats.streak}
            icon={Flame}
            className={sessionStats.streak >= 5 ? "ring-2 ring-accent" : ""}
          />
          <StatsCard 
            label="Best" 
            value={sessionStats.bestStreak}
            icon={Trophy}
          />
          <StatsCard 
            label="Accuracy" 
            value={`${accuracy}%`}
            icon={Target}
            trend={accuracy >= 80 ? { positive: true, text: 'Great!' } : null}
          />
        </div>

        {/* Flashcard Area */}
        <div className="flex flex-col items-center justify-center min-h-[50vh]" data-testid="flashcard-area">
          {/* Streak indicator */}
          <AnimatePresence>
            {sessionStats.streak >= 3 && (
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex items-center gap-2 mb-6 px-4 py-2 bg-accent/10 rounded-full"
                data-testid="streak-indicator"
              >
                <Flame className={cn(
                  "w-5 h-5 text-accent",
                  sessionStats.streak >= 5 && "animate-fire"
                )} />
                <span className="font-bold text-accent">{sessionStats.streak} streak!</span>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Current Key Indicator */}
          {currentQuestion && (
            <motion.div
              key={currentQuestion.key}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-4 text-sm text-muted-foreground"
              data-testid="current-key-indicator"
            >
              Practicing: {selectedKeys.join(', ')}
            </motion.div>
          )}

          {/* Flashcard */}
          {currentQuestion && (
            <Flashcard
              question={currentQuestion}
              onAnswer={handleAnswer}
              onNext={handleNext}
              showAnswer={showAnswer}
              setShowAnswer={setShowAnswer}
            />
          )}
        </div>

        {/* Session Controls */}
        <div className="flex justify-center gap-4 mt-8">
          <Button
            variant="outline"
            onClick={async () => {
              await saveSession();
              questionDetailsRef.current = [];
              sessionStartRef.current = Date.now();
              resetSession();
              generateNewQuestion();
            }}
            className="btn-press"
            data-testid="reset-session-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Reset Session
          </Button>
        </div>
        </>
        )}
      </main>
    </div>
  );
};

export default Practice;
